import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Card, Button, Input} from "../../../components/common";
import { Checkbox } from 'antd';
import { GlobalContext } from "../../../lib/context";
import { IconMail, IconLock, IconShieldLock } from "@tabler/icons-react";
import request from "../../../utils/request";
import { useNavigate } from 'react-router-dom';
import styles from "./styles.module.css";
import { TrialUpdate } from "../../../components/header";

type FieldType = {
  email: string;
  password: string;
  other: string;
  otp?: string;
};

const SignIn = () => {
  const { setAuth, setCurrentProject, setProjects, setProfile, setOwners } =
    useContext(GlobalContext);
  const navigate = useNavigate();
  const [error, setError] = useState<FieldType>({
    email: "",
    password: "",
    otp: "",
    other: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [resetLoading, setResetLoading] = useState<boolean>(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [showOTP, setShowOTP] = useState(false);
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [otp, setOtp] = useState("");
  const path = `${process.env.REACT_APP_BASE_URL || ""}`;
  const image_path = path + "/images/header_logo.jpg";

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const result: any = await request.post("auth/login", {
        ...credentials,
        rememberMe,
      });
      setError({ email: "", password: "", other: "", otp: "" });
      setShowOTP(true);
    } catch (error: any) {
      if (error.target && error.target.trim() !== "") {
        setError({
          email: "",
          password: "",
          other: "",
          otp: "",
          [error.target]: error.message,
        });
      } else {
        setError({
          email: "",
          password: "",
          other: error.message,
          otp: "",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOTPVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const result: any = await request.post("auth/login/verify-otp", {
        ...credentials,
        otp,
      });
      setError({ email: "", password: "", otp: "", other: "" });
      request.setToken(result.token);
      setAuth(true);
      setProfile(result.user);
      setCurrentProject(result.projects[0]);
      setProjects(result.projects);
      setOwners(result.owners);
      if (result.user.trial_remaining_days === 0) {
        navigate('/trial-update');
      }
    } catch (error: any) {
      if (error.target && error.target.trim() !== "") {
        setError(prev => ({
          ...prev,
          [error.target]: error.message,
        }));
      } else {
        const errorMessage = error.error ? (error.error + " ") : '';
        setError(prev => ({
          ...prev,
          other:  errorMessage + error.message,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    try {
      setResetLoading(true);
      await request.post("auth/login/resend-otp", credentials);
    } catch (error: any) {
      setError(prev => ({
        ...prev,
        other: "Failed to resend OTP. Please try again.",
      }));
    } finally {
      setResetLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="d-flex flex-column justify-center items-center h-screen">
      <Card width={400} className={styles.loginContainer}>
        <div className={styles.logoContainer}>
          <img
            src={image_path}
            alt="Logo"
            className={styles.logo}
            style={{ textAlign: "center" }}
          />
        </div>
        <p className="mb-6 text-center title">
          {showOTP ? "Enter OTP" : "Login to your account"}
        </p>
          
        {!showOTP ? (
          <form onSubmit={handleLogin}>
            <p className="mb-2">Email</p>
            <Input
              type="text"
              name="email"
              value={credentials.email}
              onChange={handleInputChange}
              mb={16}
              prefix={<IconMail size={18} color="#666" />}
              error={error.email}
              block
            />
            <p className="mb-2">Password</p>
            <Input
              type="password"
              name="password"
              value={credentials.password}
              onChange={handleInputChange}
              mb={24}
              prefix={<IconLock size={18} color="#666" />}
              error={error.password}
              block
            />
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="mb-16"
            >
              Keep me signed in
            </Checkbox>

            {error.other && (
              <div className="bg-danger mb-6 px-4 py-3 text-white">
                {error.other}
              </div>
            )}

            <Button type="submit" disabled={loading} block>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleOTPVerify}>
            <p className="mb-2">Enter OTP sent to your email</p>
            <Input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              mb={16}
              prefix={<IconShieldLock size={18} color="#666" />}
              error={error.otp}
              block
            />
            
            {error.other && (
              <div className="bg-danger mb-6 px-4 py-3 text-white">
                {error.other}
              </div>
            )}

            <Button type="submit" disabled={loading} className="mb-4" block>
              {loading ? 'Verifying OTP...' : 'Verify OTP'}
            </Button>
            
            <Button type="button" onClick={resendOTP} disabled={resetLoading} block>
              {resetLoading ? 'Resending OTP...' : 'Resend OTP'}
            </Button>
          </form>
        )}
      </Card>
      <Link to={`${path}/auth/reset-password`} className="mb-2 text-center text-primary">
        Password Help
      </Link>
      <p className="text-secondary">
        Don't have account?{" "}
        <Link to={`${path}/auth/plans`} className="text-primary">
          Create an Account
        </Link>
      </p>
    </div>
  );
};

export default SignIn;